'use client';

import { type ReactElement, useEffect, useState } from 'react';
import { Minus as MinusIcon, Plus as PlusIcon } from 'lucide-react';

import { Flex } from '../Flex/Flex';
import { IconButton } from '../IconButton/IconButton';
import { Input } from '../Input/Input';

import styles from './QuantitySelector.module.scss';

export interface QuantitySelectorLabels {
    /** Accessible name of the − button, e.g. "Decrease quantity". */
    decrease: string;
    /** Accessible name of the + button, e.g. "Increase quantity". */
    increase: string;
    /** Accessible name of the number field, e.g. "Quantity". */
    quantity: string;
}

export interface QuantitySelectorProps {
    /** Current quantity (controlled). */
    value: number;
    /** Called with the new quantity, already clamped to `min`…`max`. */
    onChange: (value: number) => void;
    /** @default 1 */
    min?: number;
    max?: number;
    /** @default false */
    disabled?: boolean;
    /** Translated accessible names of the buttons and the field. */
    labels: QuantitySelectorLabels;
    className?: string;
}

/**
 * − / number / + control for a quantity. The typed value is applied on blur or Enter and clamped to `min`…`max`.
 *
 * @example
 * <QuantitySelector value={quantity} onChange={setQuantity} labels={{ decrease: t('…'), increase: t('…'), quantity: t('…') }} />
 */

export function QuantitySelector({
    value,
    onChange,
    min = 1,
    max,
    disabled = false,
    labels,
    className,
}: QuantitySelectorProps): ReactElement {
    const [draft, setDraft] = useState(String(value));

    useEffect(() => {
        setDraft(String(value));
    }, [value]);

    const commit = (): void => {
        const parsed = parseInt(draft, 10);
        if (isNaN(parsed) || parsed < min) {
            onChange(min);
            setDraft(String(min));
        } else if (max !== undefined && parsed > max) {
            onChange(max);
            setDraft(String(max));
        } else {
            onChange(parsed);
            setDraft(String(parsed));
        }
    };

    return (
        <Flex align="center" gap={4} className={className}>
            <IconButton
                type="button"
                aria-label={labels.decrease}
                variant="outlined"
                color="soft"
                size="md"
                className={styles['qty-btn']}
                disabled={disabled || value <= min}
                onClick={() => onChange(Math.max(min, value - 1))}
            >
                <MinusIcon />
            </IconButton>

            <Input
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                value={draft}
                onChange={(e) => setDraft(e.target.value.replace(/\D/g, ''))}
                onBlur={commit}
                onKeyDown={(e) => e.key === 'Enter' && commit()}
                aria-label={labels.quantity}
                disabled={disabled}
                className={styles['qty-input']}
            />

            <IconButton
                type="button"
                aria-label={labels.increase}
                variant="outlined"
                color="soft"
                size="md"
                className={styles['qty-btn']}
                disabled={disabled || (max !== undefined && value >= max)}
                onClick={() => onChange(max !== undefined ? Math.min(max, value + 1) : value + 1)}
            >
                <PlusIcon />
            </IconButton>
        </Flex>
    );
}
